import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { SEOHead } from '~/components/business/SEOHead'
import { GlobalRequest } from '~/components/global-request'
import { Layout } from '~/layouts/default'
import { MyContainer } from '~/components/ui/MyContainer'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyDivide } from '~/components/ui/MyDivide'
import FollowBtn from '~/components/business/FollowBtn'
import { useStores } from '~/hooks/useStores'
import classNames from 'classnames'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import {
  getCompetitionProductDetail,
  getCompetitionProductDetailVariables,
  getCompetitionProductDetail_itemModule_artwork,
  getCompetitionProductDetail_itemModule_artwork_artworkSocialInfo,
  SocialViewType,
  ArtworkStatusEnum,
  VisibleType,
  getExpertCommentList,
  getExpertCommentListVariables,
  ArtworkDBType,
  getCompetitionDetail,
  getCompetitionDetailVariables,
  getCompetitionDetail_marketingModule_competition,
  reportView,
  reportViewVariables,
  AffectedType,
} from '~/graphqls/types'
import {
  GET_COMPETITION_PRODUCT_DETAIL,
  GET_EXPERT_COMMENT_LIST,
  GET_COMPETITION_DETAIL,
  REPORT_VIEW,
} from '~/graphqls/gqls'
import { message } from 'antd'
import Link from 'next/link'
import { to800Image, toHtml, graphQLErrorMessage } from '~/utils'
import { useMutation } from '@apollo/client'
import { useValidateAuth } from '~/hooks/useValidateAuth'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'

import styles from './[id].module.css'
import { MyAvatar } from '~/components/ui/MyAvatar'
import { MyIcon } from '~/components/ui/MyIcon'
import { ArtworkShareModal } from '~/components/business/ArtworkShareModal'
import { observer } from 'mobx-react-lite'
import { ProductDescModal } from '~/components/business/ProductDescModal'
import AdvancedInfoForImage from '~/components/pages/ai-competition/AdvancedInfoForImage'
import AdvancedInfoForVideo from '~/components/pages/ai-competition/AdvancedInfoForVideo'
import Player from '~/components/pages/ai-competition/Player'
import {
  ExpertComment,
  ExpertCommentType,
} from '~/components/pages/ai-competition/ExpertComment'
import CoverDom from '~/components/business/CoverDom'
import { VoteCaptcha } from '~/components/pages/ai-competition/VoteCaptcha'
import DraftArtworkImages from '~/components/pages/ai-competition/DraftArtworkImages'

const TEXT_LIMIT = 40

const Detail = ({
  product: _product,
  expertCommentList,
  competition,
}: {
  product: getCompetitionProductDetail_itemModule_artwork
  expertCommentList: ExpertCommentType[]
  competition: getCompetitionDetail_marketingModule_competition
}) => {
  const router = useRouter()
  const { store } = useStores()
  const [productDescVisible, setProductDescVisible] = useState(false)
  const [draftSelftDescVisible, setDraftSelfDescVisible] = useState(false)
  const [shareVisible, setShareVisible] = useState(false)
  const { validateIsLogin } = useValidateAuth()
  const [product, setProduct] = useState(_product)

  const getCompetitionDetail = useImperativeQuery<
    getCompetitionProductDetail,
    getCompetitionProductDetailVariables
  >(GET_COMPETITION_PRODUCT_DETAIL)

  /** 更新参数信息 */
  const updateAiArtworkInfo = useCallback(() => {
    getCompetitionDetail({
      id: _product?.id,
    })
      .then(data => {
        const detail = data?.data?.itemModule?.artwork || null
        if (detail) {
          setProduct(detail)
        }
      })
      .catch(err => {
        message.destroy()
        message.error(graphQLErrorMessage(err) || '获取作品参数信息失败')
      })
  }, [_product])

  /** 创作者自己 */
  const isSelf = useMemo(() => {
    return product.creator?.uid === store.userinfo?.base?.uid
  }, [product, store])

  const [viewCompetitionAction] = useMutation<reportView, reportViewVariables>(
    REPORT_VIEW
  )

  /** 线上活动30: 定制分享主题 */
  const isFestival = useMemo(() => {
    return product?.contestInfoExtra?.competitionId === 30
  }, [product])

  /** 是否为设计大赛 */
  const isDraftCompetition = useMemo(() => {
    return !!product.draftArtwork?.imageUrl
  }, [product])

  const onPrev = useCallback(() => {
    router.replace(`/ai-competition/product/${product?.contestInfoExtra?.prev}`)
  }, [router, product])
  const onNext = useCallback(() => {
    router.replace(`/ai-competition/product/${product?.contestInfoExtra?.next}`)
  }, [router, product])
  const onClose = useCallback(() => {
    router.back()
  }, [router])

  useDidUpdateEffect(() => {
    setProduct(_product)
  }, [_product])

  const onVoteSuccess = useCallback(
    (id?: number | null, vote_num?: number | null) => {
      setProduct(it => {
        return {
          ...it,
          artworkSocialInfo: {
            voted: true,
            voteCount: it?.artworkSocialInfo?.voteCount + vote_num,
          } as getCompetitionProductDetail_itemModule_artwork_artworkSocialInfo,
        }
      })
      if (
        product?.contestInfoExtra?.visibleType === VisibleType.ONLY_VOTED &&
        !isSelf
      ) {
        updateAiArtworkInfo()
      }
    },
    []
  )

  const inviteCode = store?.userinfo?.inviteCode || ''
  const qrUrl = `${process.env.NEXT_PUBLIC_MOBILE_HOST}/ai-competition/product/${product?.id}?inviteCode=${inviteCode}`
  const copyUrl = `${process.env.NEXT_PUBLIC_HOST}/ai-competition/product/${product?.id}?inviteCode=${inviteCode}`

  useEffect(() => {
    try {
      viewCompetitionAction({
        variables: {
          input: {
            id: _product?.id,
            viewType: SocialViewType.COMPETITION,
            affectedType: AffectedType.ARTWORK,
          },
        },
      })
    } catch (err) {}
  }, [])
  return (
    <Layout>
      <SEOHead title={product?.name} />
      <div
        className={styles.background}
        style={{
          backgroundImage: `url(${product.artworkUrl})`,
        }}
      >
        <div className="relative z-[2]">
          <MyContainer>
            <div className="flex justify-start items-stretch rounded-2xl overflow-hidden mx-[108px]">
              <div
                className="flex-1 h-auto flex justify-center items-center relative"
                style={{
                  background: `linear-gradient(90deg, #141416 0%, #1E1F21 100%)`,
                }}
              >
                {product.type !== ArtworkDBType.VIDEO_COMPETITION &&
                  !isDraftCompetition && (
                    <>
                      {product.gridImages?.length === 4 ? (
                        <div className="w-full h-full flex items-center">
                          <CoverDom
                            data={product.gridImages?.map(it => it!) || []}
                          />
                        </div>
                      ) : (
                        <div className="w-full flex items-center">
                          <img
                            className="w-full"
                            src={to800Image(product?.artworkUrl as string)}
                            alt=""
                          />
                        </div>
                      )}
                    </>
                  )}

                {isDraftCompetition && (
                  <DraftArtworkImages
                    images={
                      product.draftArtwork?.imageUrl?.map((url, key) => ({
                        url,
                        key,
                      })) || []
                    }
                  />
                )}

                {product.type === ArtworkDBType.VIDEO_COMPETITION && (
                  <div className="w-full">
                    <Player
                      key={product.id}
                      videoUrl={product.artworkSourceUrl!}
                      poster={product.artworkUrl!}
                      width={product.videoBaseInfo?.width!}
                      height={product.videoBaseInfo?.height!}
                    />
                  </div>
                )}

                <ExpertComment data={expertCommentList} />
              </div>

              {/* 侧边栏 */}
              <div className="w-[400px] bg-neutral-100 p-8">
                <h2 className="text-neutral-600 text-xl line-clamp-2 mb-5 break-all">
                  {product?.name}
                </h2>
                <div className="flex justify-between items-center">
                  <div className="flex justify-start items-center">
                    <Link href={`/user/${product?.creator?.uid}`}>
                      <MyAvatar
                        image={product?.creator?.avatarUrl as string}
                        isVip={product?.creator?.memberDetail?.hold}
                        size={32}
                      />
                    </Link>
                    <div className="text-neutral-800 ml-2">
                      <Link href={`/user/${product?.creator?.uid}`}>
                        {product?.creator?.nickname}
                      </Link>
                    </div>
                  </div>
                  <div>
                    <FollowBtn
                      key={product?.id}
                      targetId={product.creator?.uid}
                      status={product?.creator?.social?.followStatus}
                      onChange={status => {
                        if (
                          product?.contestInfoExtra?.visibleType ===
                            VisibleType.ONLY_FANS &&
                          !isSelf
                        ) {
                          updateAiArtworkInfo()
                        }
                      }}
                    />
                  </div>
                </div>
                <MyDivide dark={true} space={16} />
                <div className="mb-6">
                  <div className="text-sm mb-3 text-neutral-500">作品描述</div>
                  <div className="text-xs text-neutral-500 break-all">
                    {(product.description || '').length > TEXT_LIMIT ? (
                      <>
                        {(product.description || '').substring(0, TEXT_LIMIT)}
                        ...
                        <span
                          className="font-semibold text-xs text-neutral-500 ml-2 cursor-pointer"
                          onClick={() => setProductDescVisible(true)}
                        >
                          查看更多
                        </span>
                      </>
                    ) : (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: toHtml(product.description || ''),
                        }}
                      ></div>
                    )}
                  </div>
                </div>
                {/* 设计大赛自我介绍 */}
                {product.draftArtwork?.selfIntroduction &&
                  isDraftCompetition && (
                    <div className="mb-6">
                      <div className="text-sm mb-3 text-neutral-500">
                        自我介绍
                      </div>
                      <div className="text-xs text-neutral-500 break-all">
                        {(product.draftArtwork?.selfIntroduction || '').length >
                        TEXT_LIMIT ? (
                          <>
                            {(
                              product.draftArtwork?.selfIntroduction || ''
                            ).substring(0, TEXT_LIMIT)}
                            ...
                            <span
                              className="font-semibold text-xs text-neutral-500 ml-2 cursor-pointer"
                              onClick={() => setDraftSelfDescVisible(true)}
                            >
                              查看更多
                            </span>
                          </>
                        ) : (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: toHtml(
                                product.draftArtwork?.selfIntroduction || ''
                              ),
                            }}
                          ></div>
                        )}
                      </div>
                    </div>
                  )}

                <div className="flex justify-between items-center">
                  <MyBtn
                    type="default"
                    block
                    className={styles.yellowBtn}
                    onClick={() => setShareVisible(true)}
                  >
                    <div className="flex items-center justify-center">
                      <span className="w-4 h-4">
                        <MyIcon icon="sharesquare" className="text-[16px]" />
                      </span>
                      <span className="tracking-normal ml-1.5">分享好友</span>
                    </div>
                  </MyBtn>
                  {product?.artworkSocialInfo?.voted ? (
                    <MyBtn type="default" block className={styles.disabledBtn}>
                      已投票({product?.artworkSocialInfo?.voteCount})
                    </MyBtn>
                  ) : (
                    <VoteCaptcha
                      productId={product.id}
                      onVoteSuccess={onVoteSuccess}
                    >
                      <MyBtn type="primary" block>
                        投一票({product?.artworkSocialInfo?.voteCount})
                      </MyBtn>
                    </VoteCaptcha>
                  )}
                </div>
                {!isDraftCompetition && (
                  <>
                    {product.type !== ArtworkDBType.VIDEO_COMPETITION && (
                      <AdvancedInfoForImage
                        product={product}
                        key={product.id}
                      />
                    )}
                    {product.type === ArtworkDBType.VIDEO_COMPETITION && (
                      <AdvancedInfoForVideo
                        product={product}
                        key={product.id}
                        isSelf={isSelf}
                        needLinkAiCollection={competition?.needLinkAiCollection}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </MyContainer>
        </div>
        <div
          className={classNames(styles.switchBtn, 'switch-btn-close')}
          onClick={onClose}
        >
          <MyIcon icon="close" className="text-lg" />
        </div>
        {!!product?.contestInfoExtra?.prev && (
          <div
            className={classNames(styles.switchBtn, 'switch-btn-prev')}
            onClick={onPrev}
          >
            <MyIcon icon="arrow-right" className="text-lg rotate-180" />
          </div>
        )}
        {!!product?.contestInfoExtra?.next && (
          <div
            className={classNames(styles.switchBtn, 'switch-btn-next')}
            onClick={onNext}
          >
            <MyIcon icon="arrow-right" className="text-lg" />
          </div>
        )}
      </div>

      {productDescVisible && (
        <ProductDescModal
          data={product.description || ''}
          visible={productDescVisible}
          setVisible={setProductDescVisible}
          dark={true}
        />
      )}
      {draftSelftDescVisible && (
        <ProductDescModal
          data={product.draftArtwork?.selfIntroduction || ''}
          visible={draftSelftDescVisible}
          setVisible={setDraftSelfDescVisible}
          dark
          title="自我介绍"
        />
      )}
      {shareVisible && (
        <ArtworkShareModal
          dark={true}
          visible={shareVisible}
          setVisible={setShareVisible}
          options={{
            type: isFestival ? 'christmas' : 'match',
            author: product.creator?.nickname!,
            id: product?.id!,
            title: product.name!,
            imgUrl: to800Image(product.artworkUrl!),
            link: qrUrl,
            copyUrl: copyUrl,
          }}
          isVideo={product.type === ArtworkDBType.VIDEO_COMPETITION}
        />
      )}
    </Layout>
  )
}

export default observer(Detail)

export const getServerSideProps: GetServerSideProps = async context => {
  const { client, initialState } = await GlobalRequest(context)
  try {
    const id = Number(context.params?.id)

    if (Number.isNaN(id)) {
      return {
        notFound: true,
      }
    }

    const res = await client.query<
      getCompetitionProductDetail,
      getCompetitionProductDetailVariables
    >({
      query: GET_COMPETITION_PRODUCT_DETAIL,
      variables: {
        id,
      },
      fetchPolicy: 'no-cache',
    })

    const competitionRes = await client.query<
      getCompetitionDetail,
      getCompetitionDetailVariables
    >({
      query: GET_COMPETITION_DETAIL,
      variables: {
        id: res.data.itemModule?.artwork?.contestInfoExtra?.competitionId,
      },
      fetchPolicy: 'no-cache',
    })

    const expertCommentRes = await client.query<
      getExpertCommentList,
      getExpertCommentListVariables
    >({
      query: GET_EXPERT_COMMENT_LIST,
      variables: {
        id,
      },
      fetchPolicy: 'no-cache',
    })

    /** 审核通过 */
    const isAccept =
      res?.data?.itemModule?.artwork?.status === ArtworkStatusEnum.ACCEPT

    if (!isAccept) {
      return {
        notFound: true,
      }
    }
    return {
      props: {
        initialState,
        product: res?.data?.itemModule?.artwork || {},
        expertCommentList: JSON.parse(
          expertCommentRes.data.copyrightItemModule?.artwork?.contestInfoExtra
            ?.expertComments || '[]'
        ),
        competition: competitionRes.data.marketingModule?.competition,
      },
    }
  } catch (error) {
    return {
      notFound: true,
    }
  }
}
